interface DashboardRequestOptions {
  apiBase: string;
  token?: string;
  path: string;
  init?: RequestInit;
  withAuth?: boolean;
}

export const SESSION_EXPIRED_EVENT = 'nook:session-expired';

function parseApiErrorMessage(detail: string): string | undefined {
  if (!detail.trim()) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(detail) as {message?: string | string[]};
    if (Array.isArray(parsed.message) && parsed.message.length > 0) {
      return parsed.message[0];
    }
    if (typeof parsed.message === 'string' && parsed.message.trim()) {
      return parsed.message;
    }
  } catch {
    return detail.trim();
  }

  return undefined;
}

function toFriendlyApiMessage(
  path: string,
  status: number,
  apiMessage?: string,
): string {
  const lowerMessage = (apiMessage || '').toLowerCase();

  if (
    path === '/auth/login' &&
    (status === 401 || lowerMessage.includes('invalid credentials'))
  ) {
    return 'Email or password is incorrect. Please try again.';
  }

  if (path === '/auth/reset-password' && status === 404) {
    return 'Password reset is not available yet. Please contact your administrator.';
  }

  if (status === 400 || status === 422) {
    return apiMessage || 'Please check your input and try again.';
  }

  if (status === 401) {
    return 'Your session is invalid or expired. Please sign in again.';
  }

  if (status === 403) {
    return 'You do not have permission to perform this action.';
  }

  if (status === 404) {
    return 'The requested resource was not found.';
  }

  if (status === 409) {
    return 'This action conflicts with existing data.';
  }

  if (status >= 500) {
    return 'Our server is having trouble right now. Please try again shortly.';
  }

  return apiMessage || 'Request could not be completed. Please try again.';
}

export async function requestDashboard<T>({
  apiBase,
  token,
  path,
  init = {},
  withAuth = false,
}: DashboardRequestOptions): Promise<T> {
  const headers: Record<string, string> = {
    ...(init.body instanceof FormData ? {} : {'Content-Type': 'application/json'}),
    ...(init.headers as Record<string, string> | undefined),
  };

  if (withAuth && token) {
    headers.Authorization = `Bearer ${token}`;
  }

  let response: Response;

  try {
    response = await fetch(`${apiBase}${path}`, {
      ...init,
      headers,
    });
  } catch {
    throw new Error(
      'Unable to connect to the server. Please check your internet connection and try again.',
    );
  }

  if (!response.ok) {
    if (response.status === 401 && withAuth && typeof window !== 'undefined') {
      window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
    }
    const detail = await response.text();
    const apiMessage = parseApiErrorMessage(detail);
    throw new Error(toFriendlyApiMessage(path, response.status, apiMessage));
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}
